<script lang="ts">
  import {IntegrationService} from '@/services';
  import Swal from 'sweetalert2';
  import {parseIdentifierToQueryString} from '../helpers';
  import {getErrorMessage} from './ResiIO';
  import {createEventDispatcher} from 'svelte';
  import {IntegrationUser} from '@/types/integrations';

  export let clientId: string;
  export let clientSecret: string;
  export let integrationId: number;
  export let isUpdating: boolean;
  export let userIntegration: IntegrationUser | undefined;

  const dispatch = createEventDispatcher<{
    close: void;
  }>();

  let integrationService = new IntegrationService();
  let errorMessage: string;
  let loading: boolean;

  async function connect() {
    if (!clientId || !clientSecret) return;
    loading = true;
    errorMessage = '';
    const newIdentifier = parseIdentifierToQueryString({client_id: clientId, client_secret: clientSecret});
    await integrationService
      .link(newIdentifier, integrationId)
      .then(async () => {
        isUpdating = false;
        userIntegration = await new IntegrationService().getUserIntegration(integrationId);
        Swal.fire({
          title: 'Success',
          text: 'Integration linked successfully',
          icon: 'success'
        });
      })
      .catch(error => {
        errorMessage = getErrorMessage(error?.response?.data?.message);
      })
      .finally(() => {
        loading = false;
      });
  }
</script>

<div class="contents">
  <div class="flex items-center gap-x-3">
    <h1 class="text-2xl font-bold">Upload from</h1>
    <a href="https://resi.io" target="_blank">
      <img
        class="h-11"
        src="https://storage.googleapis.com/sermonshots-local-storage/images/resi-logo.svg"
        alt="Resi.io logo"
      />
    </a>
  </div>
  <div class="mt-6 space-y-6 text-sermon-base-300">
    <h3 class="text-base font-medium">How do I get a Acess Token</h3>
    <ol class="list-decimal space-y-4 pl-5">
      <li>
        <p>
          Go to <a class="text-sermon-orange" href="https://resi.io" target="_blank">Resi.io</a> and log in your account
          or
          <a class="text-sermon-orange" href="https://studio.resi.io/signup/plans" target="_blank">sign up here</a>.
        </p>
      </li>
      <li>
        To request an Access Token, you’ll first need a Client ID and Client Secret. You can get these by creating an
        <a class="text-sermon-orange" href="https://studio.resi.io/settings/api-apps" target="_blank"
          >API Client in Studio</a
        > under Settings → API Clients.
      </li>
    </ol>
  </div>
  <form class="mt-6" on:submit|preventDefault={connect}>
    <div class="flex w-full gap-x-3">
      <div class="flex w-full flex-col gap-y-2">
        <label for="clientId">Client ID</label>
        <input
          placeholder="e.g. “331026f5-2ac3-175e-ca33-d961e3370eff”"
          class={errorMessage ? 'error' : ''}
          type="text"
          name="clientId"
          autocomplete="off"
          required
          bind:value={clientId}
        />
      </div>
      <div class="flex w-full flex-col gap-y-2">
        <label for="clientSecret">Client Secret</label>
        <input
          placeholder="e.g. “Su8FBvI2aVmwjoS9FnZuxRsDuS9RJtBkEg2Gkfjf75A2s9Kt2zsCX7RpCXif8Fik”"
          class={errorMessage ? 'error' : ''}
          type="text"
          name="clientSecret"
          autocomplete="off"
          required
          bind:value={clientSecret}
        />
      </div>
    </div>
    {#if errorMessage}
      <div class="mt-4 rounded-lg bg-red-100 p-4 text-red-700">{errorMessage}</div>
    {/if}
    <div class="mt-6 flex justify-end gap-x-3">
      <button
        type="button"
        class="btn btn-secondary !border-sermon-base-300 !bg-none !text-sermon-base-300 hover:!bg-sermon-base-300/10"
        disabled={loading}
        on:click={() => dispatch('close')}>Cancel</button
      >
      <button type="submit" class="btn btn-primary" disabled={loading}>
        {#if loading}
          <i class="fa fa-spinner animate-spin" />
        {/if}
        Connect
      </button>
    </div>
  </form>
</div>

<style lang="postcss">
  label {
    @apply font-medium text-sermon-base-300;
  }
  input {
    @apply block w-full rounded-lg border border-y border-gray-300 bg-white px-4 py-2.5 text-sermon-base placeholder-gray-300 focus:border-sermon-pink focus:outline-none focus:ring focus:ring-sermon-pink focus:ring-opacity-40 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:placeholder-zinc-500 dark:focus:border-blue-300;
    &.error {
      @apply border-red-500;
    }
  }
</style>
